import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileUp,
  Lock,
  LockOpen,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrivateNotes } from "../../hooks/usePrivateNotes";
import { useServices } from "../../hooks/useServices";
import {
  decryptNotesWithPassphrase,
  type EncryptedNotesEnvelope,
  MIN_PASSPHRASE_LENGTH,
} from "../../lib/privateNotesCrypto";
import type { ImportResult } from "../../lib/privateNotesUtils";
import {
  exportEncryptedBackup,
  exportPrivateNotes,
  importFailure,
  importNotesRecord,
  importParsedPlaintext,
  parseImportFile,
} from "../../lib/privateNotesUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { PrivateNotesUnlockForm } from "./PrivateNotesUnlockForm";

interface PrivateNotesManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 暗号化セクション（平文 / ロック中 / 解除済みの 3 状態で切り替え）
 */
function EncryptionSection() {
  const { t } = useTranslation();
  const { encryptionStatus, enableEncryption, disableEncryption, resetEncryptedNotes } =
    usePrivateNotes();

  const [isEnableFormOpen, setIsEnableFormOpen] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [passphraseConfirm, setPassphraseConfirm] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isEnabling, setIsEnabling] = useState(false);

  const closeEnableForm = () => {
    setIsEnableFormOpen(false);
    setPassphrase("");
    setPassphraseConfirm("");
    setFormError(null);
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passphrase.length < MIN_PASSPHRASE_LENGTH) {
      setFormError(t("privateMemo.encryption.tooShort", { min: MIN_PASSPHRASE_LENGTH }));
      return;
    }
    if (passphrase !== passphraseConfirm) {
      setFormError(t("privateMemo.encryption.mismatch"));
      return;
    }

    setIsEnabling(true);
    setFormError(null);
    try {
      await enableEncryption(passphrase);
      closeEnableForm();
    } catch {
      setFormError(t("privateMemo.encryption.enableError"));
    } finally {
      setIsEnabling(false);
    }
  };

  const resetDialog = (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="w-full text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4 mr-2" />
          {t("privateMemo.encryption.resetButton")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("privateMemo.encryption.resetConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("privateMemo.encryption.resetConfirmDescription")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={resetEncryptedNotes} className="bg-red-600 hover:bg-red-700">
            {t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4" />
        {t("privateMemo.encryption.heading")}
      </h3>

      {encryptionStatus === "plaintext" && (
        <>
          <p className="text-xs text-gray-600">
            {t("privateMemo.encryption.plaintextDescription")}
          </p>
          {!isEnableFormOpen ? (
            <Button onClick={() => setIsEnableFormOpen(true)} className="w-full">
              <Lock className="w-4 h-4 mr-2" />
              {t("privateMemo.encryption.enableButton")}
            </Button>
          ) : (
            <form onSubmit={handleEnable} className="space-y-2">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-300">
                <p className="text-xs text-amber-900 leading-relaxed">
                  {t("privateMemo.encryption.forgotWarning")}
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="encryption-passphrase" className="text-xs">
                  {t("privateMemo.encryption.passphraseLabel", { min: MIN_PASSPHRASE_LENGTH })}
                </Label>
                <Input
                  id="encryption-passphrase"
                  type="password"
                  value={passphrase}
                  onChange={(e) => {
                    setPassphrase(e.target.value);
                    setFormError(null);
                  }}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="encryption-passphrase-confirm" className="text-xs">
                  {t("privateMemo.encryption.passphraseConfirmLabel")}
                </Label>
                <Input
                  id="encryption-passphrase-confirm"
                  type="password"
                  value={passphraseConfirm}
                  onChange={(e) => {
                    setPassphraseConfirm(e.target.value);
                    setFormError(null);
                  }}
                  autoComplete="new-password"
                />
              </div>
              {formError && (
                <p className="text-xs text-red-600" role="alert">
                  {formError}
                </p>
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={isEnabling} className="flex-1">
                  {isEnabling
                    ? t("privateMemo.encryption.enabling")
                    : t("privateMemo.encryption.enableConfirmButton")}
                </Button>
                <Button type="button" variant="outline" onClick={closeEnableForm}>
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          )}
        </>
      )}

      {encryptionStatus === "locked" && (
        <>
          <p className="text-xs text-gray-600">{t("privateMemo.encryption.lockedMessage")}</p>
          <PrivateNotesUnlockForm />
          {resetDialog}
        </>
      )}

      {encryptionStatus === "unlocked" && (
        <>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-300">
            <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-900">{t("privateMemo.encryption.enabledStatus")}</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <LockOpen className="w-4 h-4 mr-2" />
                {t("privateMemo.encryption.disableButton")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("privateMemo.encryption.disableConfirmTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("privateMemo.encryption.disableConfirmDescription")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={disableEncryption}>
                  {t("privateMemo.encryption.disableConfirmButton")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {resetDialog}
        </>
      )}
    </div>
  );
}

/**
 * プライベートメモ管理パネル（統計・暗号化・エクスポート/インポート）
 *
 * 設定ページ「プライベートメモ」タブとダイアログ（PrivateNotesManager）の両方で使う。
 * ダイアログはメモポップオーバー・暗号化ナッジからの文脈維持導線として残している
 */
export function PrivateNotesPanel() {
  const { t } = useTranslation();
  const { notes, bulkUpdateNotes, encryptionStatus, getEncryptedEnvelope } = usePrivateNotes();
  const { services } = useServices();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [importMode, setImportMode] = useState<"merge" | "overwrite">("merge");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  // v2 暗号化バックアップの取り込み待ち（パスフレーズ入力が必要）
  const [pendingEncryptedImport, setPendingEncryptedImport] =
    useState<EncryptedNotesEnvelope | null>(null);
  const [importPassphrase, setImportPassphrase] = useState("");
  const [importPassphraseError, setImportPassphraseError] = useState(false);

  const notesCount = Object.keys(notes).length;
  const isLocked = encryptionStatus === "locked";

  // エクスポート処理（平文 v1）
  const handleExport = () => {
    exportPrivateNotes(notes, services);
  };

  // 暗号化バックアップの取り込み待ち状態をクリア
  const resetEncryptedImport = () => {
    setPendingEncryptedImport(null);
    setImportPassphrase("");
    setImportPassphraseError(false);
  };

  // ファイル選択処理（v1 平文はそのまま取り込み、v2 暗号化はパスフレーズ入力へ）
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportResult(null);
    resetEncryptedImport();

    try {
      const parsed = await parseImportFile(file);
      if (parsed.kind === "encrypted") {
        // パスフレーズ入力を求める（復号は handleEncryptedImport で行う）
        setPendingEncryptedImport(parsed.envelope);
      } else {
        const validServiceIds = new Set(services.map((s) => s.id));
        const result = importParsedPlaintext(parsed.data, importMode, notes, validServiceIds);
        setImportResult(result);
        if (result.success && result.notes) {
          bulkUpdateNotes(result.notes);
        }
      }
    } catch (error) {
      setImportResult(
        importFailure(error instanceof Error ? error.message : t("privateMemo.utils.unknownError")),
      );
    } finally {
      setIsImporting(false);
      // ファイル選択をリセット（同じファイルを再度選択可能に）
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 暗号化バックアップの復号取り込み
  const handleEncryptedImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingEncryptedImport || !importPassphrase) return;

    setIsImporting(true);
    setImportPassphraseError(false);
    try {
      const { notes: decrypted } = await decryptNotesWithPassphrase(
        pendingEncryptedImport,
        importPassphrase,
      );
      const validServiceIds = new Set(services.map((s) => s.id));
      const result = importNotesRecord(decrypted, importMode, notes, validServiceIds);
      setImportResult(result);
      if (result.success && result.notes) {
        bulkUpdateNotes(result.notes);
      }
      resetEncryptedImport();
    } catch {
      // 誤パスフレーズ。入力し直せるようフォームは残す
      setImportPassphraseError(true);
    } finally {
      setIsImporting(false);
    }
  };

  const reasonLabel = (
    reason: "invalid_id" | "content_sanitized" | "too_long" | "empty_after_sanitize",
  ): string => {
    switch (reason) {
      case "invalid_id":
        return t("privateMemo.manager.errorInvalidId");
      case "content_sanitized":
        return t("privateMemo.manager.errorContentSanitized");
      case "empty_after_sanitize":
        return t("privateMemo.manager.errorEmptyAfterSanitize");
      default:
        return t("privateMemo.manager.errorTooLong");
    }
  };

  return (
    <div className="space-y-6">
      {/* 統計情報 */}
      <div>
        <p className="text-sm text-gray-600 mb-1">{t("privateMemo.manager.currentCountLabel")}</p>
        {isLocked ? (
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <Lock className="w-4 h-4" />
            {t("privateMemo.encryption.lockedStatus")}
          </p>
        ) : (
          <p className="text-2xl font-bold">
            {t("privateMemo.manager.currentCount", { count: notesCount })}
          </p>
        )}
      </div>

      {/* 暗号化 */}
      <EncryptionSection />

      {/* エクスポート */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{t("privateMemo.manager.exportHeading")}</h3>
        {encryptionStatus === "plaintext" ? (
          <>
            <p className="text-xs text-gray-600">{t("privateMemo.manager.exportDescription")}</p>
            <Button onClick={handleExport} disabled={notesCount === 0} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              {t("privateMemo.manager.exportButton")}
            </Button>
            {notesCount === 0 && (
              <p className="text-xs text-gray-500 text-center">
                {t("privateMemo.manager.exportEmpty")}
              </p>
            )}
          </>
        ) : (
          <>
            {/* 暗号化有効時の既定は暗号化バックアップ（封筒をそのまま書き出し） */}
            <p className="text-xs text-gray-600">
              {t("privateMemo.encryption.exportEncryptedDescription")}
            </p>
            <Button
              onClick={() => {
                const envelope = getEncryptedEnvelope();
                if (envelope) exportEncryptedBackup(envelope);
              }}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              {t("privateMemo.encryption.exportEncryptedButton")}
            </Button>
            {/* 平文エクスポートは解除済み状態でのみ・警告付き */}
            {encryptionStatus === "unlocked" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={notesCount === 0} className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    {t("privateMemo.encryption.exportPlainButton")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("privateMemo.encryption.exportPlainConfirmTitle")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("privateMemo.encryption.exportPlainConfirmDescription")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleExport}>
                      {t("privateMemo.encryption.exportPlainConfirmButton")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </>
        )}
      </div>

      {/* インポート */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{t("privateMemo.manager.importHeading")}</h3>
        {isLocked ? (
          <p className="text-xs text-gray-600">{t("privateMemo.encryption.importLockedMessage")}</p>
        ) : (
          <>
            <p className="text-xs text-gray-600">{t("privateMemo.manager.importDescription")}</p>

            {/* インポートモード選択 */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t("privateMemo.manager.modeLabel")}</Label>
              <RadioGroup
                value={importMode}
                onValueChange={(v) => setImportMode(v as "merge" | "overwrite")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="merge" id="mode-merge" />
                  <Label htmlFor="mode-merge" className="text-xs font-normal cursor-pointer">
                    {t("privateMemo.manager.modeMerge")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="overwrite" id="mode-overwrite" />
                  <Label htmlFor="mode-overwrite" className="text-xs font-normal cursor-pointer">
                    {t("privateMemo.manager.modeOverwrite")}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* ファイル選択ボタン */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                variant="outline"
                className="w-full"
              >
                <FileUp className="w-4 h-4 mr-2" />
                {isImporting
                  ? t("privateMemo.manager.importing")
                  : t("privateMemo.manager.selectFile")}
              </Button>
            </div>

            {/* 暗号化バックアップのパスフレーズ入力 */}
            {pendingEncryptedImport && (
              <form
                onSubmit={handleEncryptedImport}
                className="space-y-2 p-3 rounded-lg bg-gray-50 border border-gray-300"
              >
                <p className="text-xs text-gray-700">
                  {t("privateMemo.encryption.importEncryptedDetected")}
                </p>
                <Input
                  type="password"
                  value={importPassphrase}
                  onChange={(e) => {
                    setImportPassphrase(e.target.value);
                    setImportPassphraseError(false);
                  }}
                  placeholder={t("privateMemo.encryption.passphrasePlaceholder")}
                  autoComplete="off"
                  aria-label={t("privateMemo.encryption.importPassphraseLabel")}
                />
                {importPassphraseError && (
                  <p className="text-xs text-red-600" role="alert">
                    {t("privateMemo.encryption.wrongPassphrase")}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!importPassphrase || isImporting}
                    className="flex-1"
                  >
                    {t("privateMemo.encryption.importDecryptButton")}
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={resetEncryptedImport}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}

        {/* インポート結果 */}
        {importResult && (
          <div
            className={`p-3 rounded-lg border ${importResult.success ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"} space-y-2`}
          >
            <div className="flex items-center gap-2">
              {importResult.success ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-900">
                    {t("privateMemo.manager.resultSuccessTitle")}
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-semibold text-red-900">
                    {t("privateMemo.manager.resultFailureTitle")}
                  </span>
                </>
              )}
            </div>

            {importResult.success ? (
              <>
                <p className="text-xs text-green-800">
                  ✅{" "}
                  {t("privateMemo.manager.resultImported", {
                    count: importResult.imported,
                  })}
                </p>
                {importResult.skipped > 0 && (
                  <p className="text-xs text-orange-700">
                    ⚠️{" "}
                    {t("privateMemo.manager.resultSkipped", {
                      count: importResult.skipped,
                    })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-red-800">❌ {t("privateMemo.manager.resultFailed")}</p>
            )}

            {/* 警告メッセージ */}
            {importResult.warnings.length > 0 && (
              <div className="mt-2 space-y-1 border-t border-gray-300 pt-2">
                <p className="text-xs font-semibold">{t("privateMemo.manager.detailsHeading")}</p>
                {importResult.warnings.map((warning) => (
                  <p key={warning} className="text-xs text-gray-700">
                    • {warning}
                  </p>
                ))}
              </div>
            )}

            {/* エラー詳細（最初の5件のみ表示） */}
            {importResult.errors.length > 0 && importResult.success && (
              <details className="mt-2 border-t border-gray-300 pt-2">
                <summary className="text-xs font-semibold cursor-pointer">
                  {t("privateMemo.manager.skippedItemsLabel", {
                    count: importResult.errors.length,
                  })}
                </summary>
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {importResult.errors.slice(0, 5).map((error) => (
                    <p key={error.serviceId} className="text-xs text-gray-600">
                      • {error.serviceId}: {reasonLabel(error.reason)}
                    </p>
                  ))}
                  {importResult.errors.length > 5 && (
                    <p className="text-xs text-gray-500">
                      {t("privateMemo.manager.moreCount", {
                        count: importResult.errors.length - 5,
                      })}
                    </p>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* 注意事項 */}
      <div className="p-4 rounded-lg bg-red-50 border border-red-300">
        <p className="text-xs text-red-900 leading-relaxed">
          <strong>{t("privateMemo.manager.noticeLabel")}</strong>{" "}
          {t("privateMemo.manager.noticeBody")}
        </p>
      </div>
    </div>
  );
}

/**
 * プライベートメモ管理ダイアログ
 * 正式な置き場は設定ページ（/settings?tab=memo）。ここはメモポップオーバーや
 * 暗号化ナッジから「ページを離れずに」設定するための導線として残す。
 * Radix Dialog は閉じると中身をアンマウントするため、インポート途中状態は自動でリセットされる
 */
const PrivateNotesManager: React.FC<PrivateNotesManagerProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("privateMemo.manager.title")}</DialogTitle>
        </DialogHeader>
        <PrivateNotesPanel />
      </DialogContent>
    </Dialog>
  );
};

export default PrivateNotesManager;
